import { FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { demoPlaceNodes } from 'src/features/monitoringDashboardAlpha/demo/demoPlaceNodes'

type Props = {
  /** 現在選択中の現場/現場グループ UUID（混在） */
  value: string[]
  /** 適用時に呼ばれる（現場グループ＋現場の UUID をまとめて返す） */
  onChange: (placeNodeUuids: string[]) => void
}

/**
 * 現場で絞り込むためのドロップダウン（TanStack Query ベース）
 *
 * 現場グループ・現場を別々に選択できる。API (placeNodeIds) は両者の混在を受け付け、
 * 現場グループは子孫現場へ再帰展開される。
 * セールスデモ（HRMSHI-250）の間は、選択肢をユーザーの所属現場（useQueryUserPlaceNodes）でなく
 * ダミーデータの現場・現場グループにする。
 */
export const PlaceFilterDropDown = ({ value, onChange }: Props) => {
  const placeNodesData = demoPlaceNodes
  const placeGroupOptions = useMemo(
    () => (placeNodesData || []).filter(node => node.type === 'placeGroup'),
    [placeNodesData],
  )
  const placeOptions = useMemo(
    () => (placeNodesData || []).filter(node => node.type === 'place'),
    [placeNodesData],
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  // 外部の value(混在)を現場グループ／現場に振り分けてローカル保持する
  const [selectedGroupUuids, setSelectedGroupUuids] = useState<string[]>([])
  const [selectedPlaceUuids, setSelectedPlaceUuids] = useState<string[]>([])

  useEffect(() => {
    const groupUuidSet = new Set(placeGroupOptions.map(node => node.uuid))
    const placeUuidSet = new Set(placeOptions.map(node => node.uuid))
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 既存 PlaceFilterDropDown と同様のパターン
    setSelectedGroupUuids(value.filter(uuid => groupUuidSet.has(uuid)))
    setSelectedPlaceUuids(value.filter(uuid => placeUuidSet.has(uuid)))
  }, [value, placeGroupOptions, placeOptions])

  const isFilterOn = value.length > 0

  const applyFilter = () => {
    onChange([...selectedGroupUuids, ...selectedPlaceUuids])
    setAnchorEl(null)
  }

  const resetFilter = () => {
    setSelectedGroupUuids([])
    setSelectedPlaceUuids([])
    onChange([])
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        color={isFilterOn ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        現場で絞り込む: {isFilterOn ? `${value.length}件` : '全て'}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          // 選択チップが増えても画面外に隠れないよう高さを制限してスクロールさせる
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
              maxHeight: '80vh',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">現場グループ名</Typography>
            <Autocomplete
              multiple
              size="small"
              options={placeGroupOptions}
              isOptionEqualToValue={(option, val) => option.uuid === val.uuid}
              getOptionLabel={node => node.name}
              value={placeGroupOptions.filter(node =>
                selectedGroupUuids.includes(node.uuid),
              )}
              onChange={(_, selectedNodes) =>
                setSelectedGroupUuids(selectedNodes.map(node => node.uuid))
              }
              slotProps={{ chip: { size: 'small' } }}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField {...params} placeholder="現場グループを選択" />
              )}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle1">現場名</Typography>
            <Autocomplete
              multiple
              size="small"
              options={placeOptions}
              isOptionEqualToValue={(option, val) => option.uuid === val.uuid}
              getOptionLabel={node => node.name}
              value={placeOptions.filter(node =>
                selectedPlaceUuids.includes(node.uuid),
              )}
              onChange={(_, selectedNodes) =>
                setSelectedPlaceUuids(selectedNodes.map(node => node.uuid))
              }
              slotProps={{ chip: { size: 'small' } }}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField {...params} placeholder="現場を選択" />
              )}
            />
          </Stack>
          <Stack spacing={2} direction="row" justifyContent="flex-end">
            <Button onClick={resetFilter} variant="outlined" color="primary">
              リセット
            </Button>
            <Button variant="contained" color="primary" onClick={applyFilter}>
              適用
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
